import { CONTACT_PORTRAIT_SRC, FIXER_BRIEF_FORM_HASH, bookingUrl, phoneTel, whatsappUrl } from '../data/fixer-content.js'
import { useFixerContent } from '../hooks/i18n/usePageContent.js'
import { FixerBriefForm } from './FixerBriefForm.js'
import {
  FixerBriefRow,
  FixerCard,
  FixerCtaRow,
  FixerKicker,
  FixerSection,
  FixerSectionIntro,
  FixerSignalDivider,
} from './fixer-ui.js'
import { FbcDot } from './future-chat/FbcGrammar.js'

interface FixerContactSectionProps {
  year: number
}

export function FixerContactSection({ year }: FixerContactSectionProps) {
  const c = useFixerContent()
  return (
    <FixerSection id="contact">
      <div className="fixer-stack-xl">
        <div className="fixer-contact-band">
          <div className="fixer-contact-portrait-wrap">
            <img
              src={CONTACT_PORTRAIT_SRC}
              alt={c.chrome.portraitAlt}
              className="fixer-contact-portrait"
              width={320}
              height={400}
              loading="lazy"
            />
          </div>
          <div className="fixer-contact-band-copy fixer-stack-lg">
            <FixerSectionIntro
              channel="transmit"
              kicker={c.contact.number}
              title={c.contact.title}
              copy={c.contact.copy}
              titleId="fixer-contact-heading"
            />
            <p className="fixer-subheading">Farzad Bayat — {c.contact.roleLine}</p>
            <p className="fixer-body-sm">{c.contact.locationLine}</p>
            <FixerCtaRow>
              <a href={FIXER_BRIEF_FORM_HASH} className="fixer-btn-primary w-full sm:w-auto">
                {c.hero.sendBriefLabel}
              </a>
              <a href={phoneTel} className="fixer-btn-secondary w-full sm:w-auto">
                {c.hero.callLabel}
              </a>
              <a href={bookingUrl} target="_blank" rel="noreferrer" className="fixer-btn-secondary w-full sm:w-auto">
                {c.hero.bookLabel}
              </a>
              <a href={whatsappUrl} target="_blank" rel="noreferrer" className="fixer-btn-ghost w-full sm:w-auto">
                WhatsApp
              </a>
            </FixerCtaRow>
          </div>
        </div>

        <div className="fixer-stack-lg">
          <div className="fixer-card-grid-5">
            {c.contactLinks.map(link => (
              <FixerCard key={link.label} interactive>
                <a
                  href={link.href}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel={link.href.startsWith('http') ? 'noreferrer' : undefined}
                  className="flex flex-col gap-2 no-underline"
                >
                  <FixerKicker channel="signal" active={false}>{link.label}</FixerKicker>
                  <span className="fixer-subheading text-[var(--fc-accent)]">{link.value}</span>
                </a>
              </FixerCard>
            ))}
          </div>

          <div className="fixer-stack-sm max-w-3xl">
            <h3 className="fixer-subheading">{c.contact.briefTitle}</h3>
            <p className="fixer-body-sm">{c.contact.briefCopy}</p>
            <div className="flex flex-col gap-2">
              {c.briefItems.map(item => (
                <FixerBriefRow key={item}>{item}</FixerBriefRow>
              ))}
            </div>
          </div>

          <FixerBriefForm />
        </div>

        <footer className="fixer-page-footer">
          <FixerSignalDivider className="mb-6" />
          <div className="fixer-footer-row">
            <div className="flex items-center gap-2">
              <FbcDot state="signal" />
              <span>© {year} Farzad Bayat</span>
            </div>
            <nav className="fixer-footer-nav" aria-label={c.chrome.siteLinksAria}>
              {c.footerLinks.map(link => (
                <a key={link.href} href={link.href} className="fixer-footer-link">
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
          <div className="mt-3">{c.chrome.footerTagline}</div>
        </footer>
      </div>
    </FixerSection>
  )
}
